import { useId, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { isNull, isObject } from '../../../ui/utils';
import type { FilterControlProps, FilterField, FilterOption } from '../types';

const optionLabel = (option: FilterOption | unknown, key: string) =>
  isObject(option) ? String((option as FilterOption).label) : !isNull(option) ? String(option) : key;

const placeholderOf = (field: FilterField) => {
  const input = (field.controls?.input ?? {}) as { placeholder?: string };
  return input.placeholder ? input.placeholder : 'Any';
};

/** The option key whose option is `selected`, by identity or by value. */
const keyOf = (field: FilterField, optionKeys: string[], selected: unknown) => {
  if (isNull(selected)) return null;
  return (
    optionKeys.find((key) => {
      const option = field.options?.[key];
      return option === selected || (isObject(selected) && isObject(option) && (option as FilterOption).value === (selected as FilterOption).value);
    }) ?? null
  );
};

export function FilterGroup({ field, active, children }: { field: FilterField; active: boolean; children: React.ReactNode }) {
  const headingId = useId();
  return (
    <div role="group" aria-label={field.label} className="flex w-full flex-col gap-1.5">
      {field.label ? (
        <h6 id={headingId} className={cn('text-sm font-medium', active ? 'text-foreground/80' : 'text-muted-foreground')}>
          {field.label}
        </h6>
      ) : null}
      {children}
    </div>
  );
}

// SELECT field should have options obj defined with each option having a name, label, and value
// the value prop should be the selected option object or null.
export function SelectFilterControl({ field, value = null, setQuery, active }: FilterControlProps) {
  const optionKeys = useMemo(() => (isObject(field.options) ? Object.keys(field.options as object) : []), [field]);
  // if prop value is null and field has defaults.value then find option match to defaults.value
  const selectedOption = useMemo(() => {
    if (value === null && field.defaults && field.defaults.value !== undefined) {
      for (const key of optionKeys) {
        if (field.options?.[key]?.value === field.defaults.value) {
          return field.options[key];
        }
      }
      return field.defaults.value;
    }
    return value;
  }, [field, optionKeys, value]);
  const selectedKey = keyOf(field, optionKeys, selectedOption);
  const items = useMemo(() => optionKeys.map((key) => ({ value: key, label: optionLabel(field.options?.[key], key) })), [field, optionKeys]);

  return (
    <FilterGroup field={field} active={active}>
      <Select
        items={items}
        value={selectedKey}
        disabled={!optionKeys.length}
        onValueChange={(key) => {
          if (field.setField && field.options && typeof key === 'string') {
            field.setField(field.options[key], setQuery, field);
          }
        }}
      >
        <SelectTrigger aria-label={field.label} className="w-full">
          <SelectValue placeholder={placeholderOf(field)} />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FilterGroup>
  );
}

export function MultiSelectFilterControl({ field, value: valueProp = [], setQuery, active }: FilterControlProps) {
  const value: unknown[] = useMemo(() => (Array.isArray(valueProp) ? valueProp : []), [valueProp]);

  const [optionKeys, optionValues] = useMemo(() => {
    if (isObject(field.options)) {
      const optionKeys = Object.keys(field.options as object);
      const optionValues = optionKeys.map((key) => {
        const option = field.options?.[key];
        return isObject(option) ? (option as FilterOption).value : option;
      });
      return [optionKeys, optionValues] as const;
    }
    return [[] as string[], [] as unknown[]] as const;
  }, [field]);
  // if prop value is null and field has defaults.value then find option match to defaults.value
  const selectedOptions = useMemo(() => {
    const defaults = field.defaults?.value;
    if (!value.length && Array.isArray(defaults)) {
      if (optionKeys.length && defaults.length) {
        const defaultOptions = [];
        for (const defaultVal of defaults) {
          const indexOfValue = optionValues.indexOf(defaultVal);
          if (indexOfValue !== -1) {
            defaultOptions.push(field.options?.[optionKeys[indexOfValue] as string]);
          }
        }
        return defaultOptions;
      }
      return defaults;
    }
    return value;
  }, [field, optionKeys, optionValues, value]);
  const selectedKeys = selectedOptions.map((option) => keyOf(field, [...optionKeys], option)).filter((key): key is string => key !== null);
  const items = useMemo(() => optionKeys.map((key) => ({ value: key, label: optionLabel(field.options?.[key], key) })), [field, optionKeys]);

  return (
    <FilterGroup field={field} active={active}>
      <Select
        multiple
        items={items}
        value={selectedKeys}
        disabled={!optionKeys.length}
        onValueChange={(keys) => {
          if (field.setField && field.options && Array.isArray(keys)) {
            field.setField(
              keys.map((key) => field.options?.[key as string]),
              setQuery,
              field
            );
          }
        }}
      >
        <SelectTrigger aria-label={field.label} className="w-full">
          <SelectValue placeholder={placeholderOf(field)}>
            {(keys: string[]) =>
              keys.length ? keys.map((key) => items.find((item) => item.value === key)?.label ?? key).join(', ') : placeholderOf(field)
            }
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FilterGroup>
  );
}
